import axios from 'axios';
import { v4 as uuidv4 } from 'uuid'; // If uuid is not available, we'll handle it
import api from './api';

// Base URL for API calls - driven by REACT_APP_API_BASE_URL when available
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';
const API_V1_URL = `${API_BASE_URL}/api/v1`;
// Removed unused API_TEST_URL

// Flag to enable client-side mocking (no server needed)
const USE_CLIENT_MOCK = false; // Set to false to use real API

// Additional endpoint options to try if the primary one fails
const ENDPOINT_OPTIONS = [
  '/screenplay-analysis',           // Standard endpoint
  '/screenplayanalysis',            // Alternative without hyphen
  '/screenplay_analysis',           // Alternative with underscore
  '/screenplayAnalysis'             // Alternative with camelCase
];

// Enhanced logging function for debugging (only logs errors)
const logApiAttempt = (method, endpoint, success, error = null) => {
  if (!success && error) {
    console.error(`[ScreenplayAnalysis API] ${method} ${endpoint} - FAILED`, error.message);
  }
};

// Store mock analyses in memory (client-side only)
const mockAnalyses = new Map();

/**
 * Generate a mock ID if uuid is not available
 * @returns {string} - A mock ID
 */
const generateMockId = () => {
  try {
    return uuidv4();
  } catch (error) {
    // If uuid is not available, generate a random string
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }
};

/**
 * Generate a mock screenplay analysis
 * @param {string} scriptId - The ID of the script
 * @param {string} projectId - The ID of the project
 * @param {string} status - The status of the analysis
 * @returns {Object} - A mock screenplay analysis
 */
const generateMockAnalysis = (scriptId, projectId, status = 'completed') => {
  const analysisId = generateMockId();
  const createdAt = new Date();
  
  // Create a mock analysis with realistic data
  const mockAnalysis = {
    _id: analysisId,
    scriptId,
    projectId,
    status,
    createdAt: createdAt.toISOString(),
    updatedAt: createdAt.toISOString(),
    analysis: status === 'completed' ? {
      title: 'Screenplay Analysis Report',
      characters: [
        { name: 'John Smith', description: 'Main protagonist, a determined detective', scenes: 28 },
        { name: 'Sarah Johnson', description: 'Brilliant scientist with a mysterious past', scenes: 22 },
        { name: 'Dr. Marcus', description: 'Antagonist, corporate executive with hidden agenda', scenes: 15 },
        { name: 'Officer Rodriguez', description: 'Loyal friend and colleague to John', scenes: 12 }
      ],
      scenes: [
        { number: 1, description: 'Opening scene in the city at night', characters: ['John Smith'] },
        { number: 2, description: 'Laboratory where Sarah is working on her research', characters: ['Sarah Johnson', 'Dr. Marcus'] },
        { number: 3, description: 'Police station briefing', characters: ['John Smith', 'Officer Rodriguez'] }
      ],
      themes: ['Justice', 'Betrayal', 'Redemption', 'Scientific Ethics'],
      summary: 'A well-structured screenplay with strong character development and engaging plot. The pacing is good, with effective use of tension and release. The dialogue is natural and serves the story well. The themes are explored thoughtfully throughout the narrative.',
      filmCoverageReport: {
        title: 'Project Analysis',
        genre: 'Sci-Fi Thriller',
        logline: 'A determined detective teams up with a brilliant scientist to uncover a corporate conspiracy that threatens to change the course of human evolution.',
        synopsis: 'Detective John Smith investigates a series of mysterious deaths linked to a powerful biotech corporation. He discovers Dr. Sarah Johnson, a former employee who has evidence of illegal human experimentation. As they work together to expose the truth, they become targets of the corporation\'s ruthless security team led by Dr. Marcus. With time running out and powerful forces against them, John and Sarah must navigate a web of deception and danger to reveal the conspiracy before it\'s too late.',
        setting: 'Near-future urban environment with advanced technology integrated into everyday life.',
        tone: 'Tense and suspenseful with moments of philosophical reflection on the ethics of scientific advancement.',
        ratingEstimate: 'PG-13',
        marketability: 'High',
        overallEvaluation: 'Recommend',
        strengths: [
          'Strong, well-developed main characters with clear motivations',
          'Engaging plot with effective pacing and tension',
          'Thought-provoking themes that resonate with contemporary issues',
          'Visual storytelling opportunities with the futuristic setting'
        ],
        weaknesses: [
          'Some supporting characters could be more developed',
          'The third act resolution feels slightly rushed',
          'A few plot points rely on coincidence rather than character choices'
        ],
        recommendations: [
          'Consider expanding the role of Officer Rodriguez to create more emotional stakes',
          'The final confrontation scene could benefit from additional buildup',
          'Explore the ethical dilemmas faced by Sarah in more depth'
        ]
      },
      comparableFilms: [
        {
          title: 'Inception',
          year: 2010,
          director: 'Christopher Nolan',
          boxOffice: 836800000,
          budget: 160000000,
          similarities: 'Complex narrative structure, exploration of scientific concepts, action elements'
        },
        {
          title: 'Ex Machina',
          year: 2014,
          director: 'Alex Garland',
          boxOffice: 36900000,
          budget: 15000000,
          similarities: 'Intimate sci-fi thriller, ethical questions about technology, small cast'
        },
        {
          title: 'Minority Report',
          year: 2002,
          director: 'Steven Spielberg',
          boxOffice: 358400000,
          budget: 102000000,
          similarities: 'Detective protagonist, near-future setting, ethical dilemmas'
        }
      ],
      marketAnalysis: {
        targetAudience: 'Adults 18-49, particularly those interested in science fiction, thrillers, and ethical dilemmas in technology',
        boxOfficePotential: 'Medium to high, especially with the right marketing emphasizing the thriller aspects',
        franchisePotential: 'Good potential for a sequel or limited series adaptation',
        streamingPotential: 'High appeal for streaming platforms seeking original content with both commercial and critical appeal',
        internationalAppeal: 'Strong international appeal due to universal themes and visual storytelling',
        marketingRecommendations: [
          'Emphasize the thriller elements in trailers to attract mainstream audiences',
          'Highlight the ethical questions in promotional materials for more thoughtful viewers',
          'Focus on the chemistry between the two leads in character posters and teasers'
        ]
      }
    } : null
  };
  
  return mockAnalysis;
};

/**
 * Helper function to create a mock analysis (client-side only)
 */
const createMockAnalysis = (scriptId, projectId) => {
  console.log('Creating mock screenplay analysis');
  
  // Generate a unique ID for the analysis
  const analysisId = uuidv4();
  
  // Create a mock analysis object
  const mockAnalysis = {
    _id: analysisId,
    scriptId,
    projectId,
    status: 'processing',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  // Store the mock analysis
  mockAnalyses.set(analysisId, mockAnalysis);
  
  // Simulate processing delay and completion
  setTimeout(() => {
    const updatedAnalysis = {
      ...mockAnalysis,
      status: 'completed',
      updatedAt: new Date().toISOString(),
      report: {
        title: 'Mock Screenplay Analysis',
        logline: 'A mock analysis of a screenplay for development purposes.',
        synopsis: 'This is a mock synopsis generated for testing purposes.',
        strengths: ['Strong character development', 'Engaging plot', 'Good pacing'],
        weaknesses: ['Some dialogue needs work', 'Third act could be stronger'],
        marketPotential: 'Medium to high',
        targetAudience: 'Adults 18-49',
        genre: 'Drama/Thriller',
        comparableFilms: [
          { title: 'The Godfather', year: 1972 },
          { title: 'Goodfellas', year: 1990 },
          { title: 'The Departed', year: 2006 }
        ]
      }
    };
    
    // Update the stored mock analysis
    mockAnalyses.set(analysisId, updatedAnalysis);
    console.log('Mock screenplay analysis completed:', updatedAnalysis);
  }, 3000);
  
  return mockAnalysis;
};

/**
 * Client-side mock implementation (no server needed)
 * @param {string} method - HTTP method
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request data
 * @returns {Promise<Object>} - Mock response
 */
const clientSideMock = async (method, endpoint, data = null) => {
  console.log(`[CLIENT MOCK] ${method.toUpperCase()} ${endpoint}`, data);
  
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Handle different endpoints and methods
  if (endpoint.includes('/screenplay-analysis')) {
    // POST - Create a new analysis
    if (method.toLowerCase() === 'post' && !endpoint.includes('/retry')) {
      const { scriptId, projectId } = data || {};
      
      if (!scriptId || !projectId) {
        throw new Error('Missing required fields: scriptId and projectId');
      }
      
      // Create a processing analysis
      const mockAnalysis = generateMockAnalysis(scriptId, projectId, 'processing');
      
      // Store it in our mock database
      mockAnalyses.set(mockAnalysis._id, mockAnalysis);
      
      // After 5 seconds, update the status to completed
      setTimeout(() => {
        const updatedAnalysis = generateMockAnalysis(scriptId, projectId, 'completed');
        updatedAnalysis._id = mockAnalysis._id; // Keep the same ID
        mockAnalyses.set(updatedAnalysis._id, updatedAnalysis);
        console.log(`[CLIENT MOCK] Analysis ${mockAnalysis._id} updated to completed`);
      }, 5000);
      
      return { data: mockAnalysis };
    }
    
    // GET - Get all analyses for a project
    if (method.toLowerCase() === 'get' && endpoint.includes('/project/')) {
      const projectId = endpoint.split('/project/')[1];
      
      // Return all analyses for this project
      const projectAnalyses = Array.from(mockAnalyses.values())
        .filter(analysis => analysis.projectId === projectId);
      
      return { data: projectAnalyses };
    }
    
    // GET - Get analysis for a script
    if (method.toLowerCase() === 'get' && endpoint.includes('/script/')) {
      const scriptId = endpoint.split('/script/')[1];
      
      // Return the analysis for this script
      const scriptAnalysis = Array.from(mockAnalyses.values())
        .find(analysis => analysis.scriptId === scriptId);
      
      return { data: scriptAnalysis || null };
    }
    
    // GET - Get analysis by ID
    if (method.toLowerCase() === 'get' && !endpoint.includes('/project/') && !endpoint.includes('/script/')) {
      const analysisId = endpoint.split('/screenplay-analysis/')[1];
      
      // Return the analysis with this ID
      const analysis = mockAnalyses.get(analysisId);
      
      if (!analysis) {
        return { data: null };
      }
      
      return { data: analysis };
    }
    
    // DELETE - Delete an analysis
    if (method.toLowerCase() === 'delete') {
      const analysisId = endpoint.split('/screenplay-analysis/')[1];
      
      // Delete the analysis
      mockAnalyses.delete(analysisId);
      
      return { data: { message: 'Screenplay analysis deleted successfully' } };
    }
    
    // POST - Retry an analysis
    if (method.toLowerCase() === 'post' && endpoint.includes('/retry')) {
      const analysisId = endpoint.split('/screenplay-analysis/')[1].split('/retry')[0];
      const analysis = mockAnalyses.get(analysisId);
      
      if (!analysis) {
        throw new Error('Analysis not found');
      }
      
      // Update the status to processing
      analysis.status = 'processing';
      mockAnalyses.set(analysisId, analysis);
      
      // After 5 seconds, update the status to completed
      setTimeout(() => {
        const updatedAnalysis = generateMockAnalysis(analysis.scriptId, analysis.projectId, 'completed');
        updatedAnalysis._id = analysisId; // Keep the same ID
        mockAnalyses.set(analysisId, updatedAnalysis);
        console.log(`[CLIENT MOCK] Analysis ${analysisId} updated to completed`);
      }, 5000);
      
      return { data: { message: 'Screenplay analysis retry initiated', status: 'processing' } };
    }
  }
  
  // Default mock response
  return {
    data: {
      message: '[CLIENT MOCK] Operation completed successfully',
      timestamp: new Date().toISOString()
    }
  };
};

/**
 * Direct API call
 * @param {string} method - HTTP method (post, get, delete)
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request data
 * @param {Object} params - URL parameters
 * @returns {Promise<Object>} - API response
 */
// Removed unused directApiCall helper

/**
 * Creates a new screenplay analysis for a script
 * @param {string} scriptId - The ID of the script to analyze
 * @param {string} projectId - The ID of the project the script belongs to
 * @param {string} token - Authentication token (optional, will use localStorage if not provided)
 * @returns {Promise<Object>} - The created analysis
 */
const createScreenplayAnalysis = async (scriptId, projectId, token = null) => {
  console.log(`Creating screenplay analysis for script ${scriptId} in project ${projectId}`);
  
  // If client-side mock is enabled, use that
  if (USE_CLIENT_MOCK) {
    return createMockAnalysis(scriptId, projectId);
  }
  
  // Get auth token if not provided
  const authToken = token || localStorage.getItem('token');
  if (!authToken) {
    console.warn('No authentication token found');
  }
  
  // Try multiple approaches in sequence
  let lastError = null;
  
  // Approach 1: Try API utility with different endpoint options
  for (const endpoint of ENDPOINT_OPTIONS) {
    try {
      console.log(`Attempting to create screenplay analysis using API utility with endpoint: ${endpoint}`);
      const response = await api.post(endpoint, {
        scriptId,
        projectId
      });
      
      console.log('Screenplay analysis created successfully');
      logApiAttempt('POST', endpoint, true);
      return response.data;
    } catch (error) {
      console.error(`Error with endpoint ${endpoint}:`, error.message);
      logApiAttempt('POST', endpoint, false, error);
      lastError = error;
      // Continue to next endpoint
    }
  }
  
  // Approach 2: Try direct API calls with different endpoint options
  for (const endpoint of ENDPOINT_OPTIONS) {
    try {
      console.log(`Attempting direct API call with endpoint: ${endpoint}`);
      const response = await axios.post(
        `${API_V1_URL}${endpoint}`,
        {
          scriptId,
          projectId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : undefined,
            'x-auth-token': authToken
          },
          withCredentials: true
        }
      );
      
      console.log('Screenplay analysis created successfully with direct API call');
      logApiAttempt('POST', endpoint, true);
      return response.data;
    } catch (error) {
      console.error(`Error with direct call to ${endpoint}:`, error.message);
      logApiAttempt('POST', endpoint, false, error);
      // Continue to next endpoint
    }
  }
  
  // Approach 3: Try the direct test endpoint
  try {
    console.log('Attempting to create screenplay analysis using direct test endpoint');
    
    // Get the script content if possible
    let scriptContent = null;
    try {
      // Try to get script content from the page if available
      const scriptElement = document.querySelector('#script-content');
      if (scriptElement && scriptElement.textContent) {
        scriptContent = scriptElement.textContent;
        console.log('Found script content in the DOM');
      }
    } catch (scriptError) {
      console.error('Error getting script content from DOM:', scriptError);
    }
    
    const response = await axios.post(
      `${API_V1_URL}/screenplay-analysis-direct`,
      {
        scriptId,
        projectId,
        scriptContent
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Screenplay analysis created successfully using direct test endpoint');
    logApiAttempt('POST', '/screenplay-analysis-direct', true);
    return response.data;
  } catch (testError) {
    console.error('Direct test endpoint failed:', testError.message);
    logApiAttempt('POST', '/screenplay-analysis-direct', false, testError);
    lastError = testError;
  }
  
  // Approach 4: Last resort - create a client-side mock
  if (false) { // Always disable mock fallback to ensure we use the real API
    console.log('All server methods failed. Creating client-side mock as fallback');
    return createMockAnalysis(scriptId, projectId);
  }
  
  // If all approaches fail, throw an error
  console.error('All attempts to create screenplay analysis failed');
  throw new Error(lastError ? `Failed to create screenplay analysis: ${lastError.message}` : 'Failed to create screenplay analysis');
};

/**
 * Gets a screenplay analysis by ID
 * @param {string} analysisId - The ID of the analysis to get
 * @param {string} projectId - The ID of the project
 * @returns {Promise<Object>} - The analysis
 */
const getScreenplayAnalysis = async (analysisId, projectId) => {
  console.log(`Getting screenplay analysis ${analysisId} for project ${projectId}`);
  
  // If client-side mock is enabled, use that
  if (USE_CLIENT_MOCK) {
    return clientSideMock('get', `/screenplay-analysis/${analysisId}`, { projectId });
  }
  
  // Get auth token
  const authToken = localStorage.getItem('token');
  if (!authToken) {
    console.warn('No authentication token found');
  }
  
  // Try multiple approaches in sequence
  let lastError = null;
  
  // Approach 1: Try API utility with different endpoint options
  for (const endpoint of ENDPOINT_OPTIONS) {
    try {
      console.log(`Attempting to get screenplay analysis using API utility with endpoint: ${endpoint}/${analysisId}`);
      const response = await api.get(`${endpoint}/${analysisId}`, {
        params: { projectId }
      });
      
      console.log('Screenplay analysis retrieved successfully');
      logApiAttempt('GET', `${endpoint}/${analysisId}`, true);
      return response.data;
    } catch (error) {
      console.error(`Error with endpoint ${endpoint}:`, error.message);
      logApiAttempt('GET', `${endpoint}/${analysisId}`, false, error);
      lastError = error;
      // Continue to next endpoint
    }
  }
  
  // Approach 2: Try direct API calls with different endpoint options
  for (const endpoint of ENDPOINT_OPTIONS) {
    try {
      console.log(`Attempting direct API call with endpoint: ${endpoint}/${analysisId}`);
      const response = await axios.get(
        `${API_V1_URL}${endpoint}/${analysisId}`,
        {
          params: { projectId },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : undefined,
            'x-auth-token': authToken
          },
          withCredentials: true
        }
      );
      
      console.log('Screenplay analysis retrieved successfully with direct API call');
      logApiAttempt('GET', `${endpoint}/${analysisId}`, true);
      return response.data;
    } catch (error) {
      console.error(`Error with direct call to ${endpoint}:`, error.message);
      logApiAttempt('GET', `${endpoint}/${analysisId}`, false, error);
      // Continue to next endpoint
    }
  }
  
  // If all approaches fail and we're in development, return a mock
  if (USE_CLIENT_MOCK || window.location.hostname === 'localhost') {
    console.log('All server methods failed. Creating client-side mock as fallback');
    return {
      _id: analysisId,
      scriptId: 'mock-script-id',
      projectId: projectId,
      status: 'completed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      filmCoverageReport: {
        title: 'Mock Screenplay Analysis',
        logline: 'This is a mock screenplay analysis generated client-side because the server request failed.',
        synopsis: 'This mock analysis was generated because all attempts to retrieve the actual analysis from the server failed. This is a fallback to prevent the application from crashing.',
        genre: 'Drama',
        tone: 'Serious',
        setting: 'Modern day',
        characters: [
          { name: 'Character 1', description: 'Main character' },
          { name: 'Character 2', description: 'Supporting character' }
        ],
        strengths: 'N/A - Mock data',
        weaknesses: 'N/A - Mock data',
        audienceAppeal: 'General audience',
        marketability: 'Moderate',
        ratingEstimate: 'PG-13',
        overallEvaluation: 'Consider'
      },
      comparableFilms: [],
      comparableAnalytics: {
        averageBudget: 0,
        averageDomesticBoxOffice: 0,
        averageInternationalBoxOffice: 0,
        averageCombinedBoxOffice: 0,
        averageOpeningWeekendScreens: 0,
        averageTotalScreens: 0,
        dominantRating: 'PG-13',
        averageCastProminence: 'B-list'
      },
      marketAnalysis: {
        targetAudience: 'Adults 18-49, particularly those interested in science fiction, thrillers, and ethical dilemmas in technology',
        boxOfficePotential: 'Medium to high, especially with the right marketing emphasizing the thriller aspects',
        franchisePotential: 'Good potential for a sequel or limited series adaptation',
        streamingPotential: 'High appeal for streaming platforms seeking original content with both commercial and critical appeal',
        internationalAppeal: 'Strong international appeal due to universal themes and visual storytelling',
        marketingRecommendations: [
          'Emphasize the thriller elements in trailers to attract mainstream audiences',
          'Highlight the ethical questions in promotional materials for more thoughtful viewers',
          'Focus on the chemistry between the two leads in character posters and teasers'
        ]
      }
    };
  }
  
  // If all approaches fail, throw an error
  console.error('All attempts to get screenplay analysis failed');
  throw new Error(lastError ? `Failed to retrieve screenplay analysis: ${lastError.message}` : 'Failed to retrieve screenplay analysis');
};

/**
 * Gets all screenplay analyses for a project
 * @param {string} projectId - The ID of the project
 * @param {string} token - Authentication token (optional, will use localStorage if not provided)
 * @returns {Promise<Array>} - Array of screenplay analyses
 */
const getProjectScreenplayAnalyses = async (projectId, token = null) => {
  if (USE_CLIENT_MOCK) {
    return getMockProjectAnalyses(projectId);
  }
  
  // Get token from localStorage if not provided
  if (!token) {
    token = localStorage.getItem('token');
  }
  
  try {
    // Try using the API utility first
    const response = await api.get(`/screenplay-analysis/project/${projectId}`);
    return response.data;
  } catch (apiError) {
    // If API utility fails, try direct request with explicit auth
    try {
      const authResponse = await axios.get(
        `${API_V1_URL}/screenplay-analysis/project/${projectId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
          },
          withCredentials: true
        }
      );
      return authResponse.data;
    } catch (authError) {
      console.error('Error getting screenplay analyses:', authError.response?.data || authError.message);
      // If all server methods fail, fall back to client-side mock
      return getMockProjectAnalyses(projectId);
    }
  }
};

/**
 * Helper function to get mock analyses for a project (client-side only)
 */
const getMockProjectAnalyses = (projectId) => {
  console.log('Getting mock screenplay analyses for project:', projectId);
  
  // Filter the mock analyses by project ID
  const projectAnalyses = Array.from(mockAnalyses.values())
    .filter(analysis => analysis.projectId === projectId);
  
  return projectAnalyses;
};

/**
 * Gets a screenplay analysis for a script
 * @param {string} scriptId - The ID of the script
 * @returns {Promise<Object>} - The screenplay analysis
 */
const getScriptScreenplayAnalysis = async (scriptId) => {
  if (USE_CLIENT_MOCK) {
    const response = await clientSideMock('get', `/screenplay-analysis/script/${scriptId}`);
    return response.data;
  }
  
  try {
    // Get the authentication token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    // Make the API call with proper authentication headers
    const response = await axios.get(
      `${API_V1_URL}/screenplay-analysis/script/${scriptId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        withCredentials: true
      }
    );
    
    return response.data;
  } catch (error) {
    // A missing analysis is a normal state (analysis hasn't been created yet)
    // Check this before logging to avoid noisy console output for expected 404s
    if (error?.response?.status === 404) {
      return null;
    }
    
    console.error('Error getting script screenplay analysis:', error.response?.data || error.message);

    // Try a direct approach as a fallback
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios({
        method: 'get',
        url: `${API_V1_URL}/screenplay-analysis/script/${scriptId}`,
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        withCredentials: true
      });
      
      return response.data;
    } catch (fallbackError) {
      // Preserve the "missing analysis" behavior for the fallback path too
      if (fallbackError?.response?.status === 404) {
        return null;
      }
      console.error('Fallback also failed:', fallbackError.response?.data || fallbackError.message);
      throw fallbackError;
    }
  }
};

/**
 * Deletes a screenplay analysis
 * @param {string} analysisId - The ID of the analysis to delete
 * @param {string} projectId - The ID of the project
 * @returns {Promise<Object>} - The response
 */
const deleteScreenplayAnalysis = async (analysisId, projectId) => {
  console.log('Deleting screenplay analysis:', analysisId, 'for project:', projectId);
  
  if (USE_CLIENT_MOCK) {
    const response = await clientSideMock('delete', `/screenplay-analysis/${analysisId}`);
    return response.data;
  }
  
  try {
    // Get the authentication token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    // Make the API call with proper authentication headers
    const response = await axios.delete(
      `${API_V1_URL}/screenplay-analysis/${analysisId}?projectId=${projectId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        withCredentials: true
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deleting screenplay analysis:', error.response?.data || error.message);
    
    // Try a direct approach as a fallback
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios({
        method: 'delete',
        url: `${API_V1_URL}/screenplay-analysis/${analysisId}`,
        params: { projectId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        withCredentials: true
      });
      
      return response.data;
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError.response?.data || fallbackError.message);
      throw fallbackError;
    }
  }
};

/**
 * Retry a screenplay analysis
 * @param {string} analysisId - ID of the analysis to retry
 * @returns {Promise<Object>} - The updated analysis
 */
const retryAnalysis = async (analysisId) => {
  try {
    // First attempt with the API utility
    try {
      const response = await api.post(`/screenplay-analysis/${analysisId}/retry`);
      return response.data;
    } catch (apiError) {
      // Fall back to direct axios call with CORS handling
      const token = localStorage.getItem('token');
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authentication headers
      if (token) {
        headers['x-auth-token'] = token;
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.post(
        `${API_V1_URL}/screenplay-analysis/${analysisId}/retry`,
        {},
        {
          headers,
          withCredentials: true
        }
      );
      
      return response.data;
    }
  } catch (error) {
    console.error('Failed to retry analysis:', error);
    throw error;
  }
};

/**
 * Force complete all pending screenplay analyses
 * @returns {Promise<Object>} - Result of the operation
 */
const forceCompleteAllAnalyses = async () => {
  try {
    // First attempt with the API utility
    try {
      const response = await api.post('/screenplay-analysis/force-complete-all');
      return response.data;
    } catch (apiError) {
      // Fall back to direct axios call with CORS handling
      const token = localStorage.getItem('token');
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authentication headers
      if (token) {
        headers['x-auth-token'] = token;
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.post(
        `${API_V1_URL}/screenplay-analysis/force-complete-all`,
        {},
        {
          headers,
          withCredentials: true
        }
      );
      
      return response.data;
    }
  } catch (error) {
    console.error('Failed to force complete all analyses:', error);
    throw error;
  }
};

// Export the functions
export {
  createScreenplayAnalysis,
  getScreenplayAnalysis,
  getProjectScreenplayAnalyses,
  getScriptScreenplayAnalysis,
  deleteScreenplayAnalysis,
  retryAnalysis,
  forceCompleteAllAnalyses
};

// Default export for backward compatibility
const screenplayAnalysisService = {
  createScreenplayAnalysis,
  getScreenplayAnalysis,
  getProjectScreenplayAnalyses,
  getScriptScreenplayAnalysis,
  deleteScreenplayAnalysis,
  retryAnalysis,
  forceCompleteAllAnalyses
};
export default screenplayAnalysisService;
